<template>
  <div class="col-12 col-xl-12 col-md-12" style="margin-top: 1em">
    <div v-for="pres in prescription" :key="pres._id">
      <div class="text-center" v-if="hasPrescription(pres)">
        <div class="row">
          <div class="col-12 mt-5">
            {{ $t("message.Nothing prescribed during the appointment") }}
          </div>
          <div class="col-12 mt-5">
            <not-found-svg style="width: 100%; height: auto;"></not-found-svg>
          </div>
        </div>
      </div>

      <div class="col-12 pb-1 mb-4 text-center" style="border-bottom: 1px solid #00000045" v-if="hasPrescription(pres)">
        <i class="fa fa-user-md"></i>
        &nbsp;
        {{ $t("message.Dr") }}. {{ provider.firstName }}
        {{ provider.lastName }},
        <span v-if="provider.description">{{ provider.description }} -</span>
        {{ provider.specialityText }}
        <br />
        <span v-if="provider.registration.number">{{ provider.registration.number }} -</span>
        {{ provider.registration.city }}
      </div>

      <div class="row" v-if="pres.prescriptions.descriptiveNotes">
        <div class="col-12 col-md-12">
          <h5>{{ $t("message.Descriptive Notes") }}</h5>
        </div>
        <div class="col-12 col-md-12">
          <card>{{ pres.prescriptions.descriptiveNotes }}</card>
        </div>
      </div>
      <br />

      <!-- Provisional Diagnosis -->
      <div class="row" v-if="pres.prescriptions.provisionalDiagnosis.notes ||
        pres.prescriptions.provisionalDiagnosis.icdCodes.length > 0
        ">
        <div class="col-12 col-md-12">
          <h5>{{ $t("message.Diagnosis or Provisional Diagnosis") }}</h5>
        </div>
        <div class="col-12 col-md-12">
          <div class="row">
            <div class="col-12 col-md-12" v-if="pres.prescriptions.provisionalDiagnosis.notes">
              <span style="font-size: large">{{
                $t("message.Provisonal Notes")
              }}</span>
            </div>
            <div class="col-12 col-md-12">
              <card>{{ pres.prescriptions.provisionalDiagnosis.notes }}</card>
            </div>
            <div class="col-12 col-md-12" v-if="pres.prescriptions.provisionalDiagnosis.icdCodes.length > 0">
              <span style="font-size: large">{{
                $t("message.ICD Codes")
              }}</span>
            </div>
            <div class="col-12 col-md-12">
              <div class="row">
                <div class="col-12 col-md-12">
                  <card v-for="icdCode in pres.prescriptions.provisionalDiagnosis
                    .icdCodes" :key="icdCode.Code">
                    <div class="col-12 col-md-12">
                      <label class="label-style">{{
                        $t("message.Code")
                      }}</label>
                    </div>
                    <div class="col-12 col-md-12">
                      <div>{{ icdCode.Code }}</div>
                    </div>
                    <div class="col-12 col-md-12">
                      <label class="label-style">{{
                        $t("message.Description")
                      }}</label>
                    </div>
                    <div class="col-12 col-md-12">
                      <div>{{ icdCode.Description }}</div>
                    </div>
                  </card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Rx Prescription -->
      <div class="row" v-if="pres.prescriptions.rx.length > 0">
        <div class="col-12 col-md-12">
          <h5>{{ $t("message.RX Prescription") }}</h5>
          <card v-for="rx in pres.prescriptions.rx" :key="rx.xyz">
            <div class="row">
              <div class="col-8 col-md-6">
                <div class="row">
                  <div class="col-12 col-md-12">
                    <label class="label-style">{{ $t("message.Name") }}</label>
                  </div>
                  <div class="col-12 col-md-12">
                    <div>{{ rx.name }}</div>
                  </div>
                </div>
              </div>
              <div class="col-4 col-md-6">
                <div class="row">
                  <div class="col-12 col-md-12">
                    <label class="label-style">{{
                      $t("message.Drug Form")
                    }}</label>
                  </div>
                  <div class="col-12 col-md-12">
                    <span>{{ getShortDosageName(rx.dosageName) }}</span>
                  </div>
                </div>
              </div>
            </div>
            <br />
            <div class="col-12 col-md-12">
              <div class="row">
                <div class="col-12 col-md-12">
                  <label class="label-style">{{
                    $t("message.Dosage")
                  }}</label>
                </div>
                <div class="col-3 col-md-3">
                  <div class="row">
                    <div class="col-12 col-md-12">
                      <label class="label-style">{{
                        $t("message.Morning")
                      }}</label>
                    </div>
                    <div class="col-12 col-md-12">
                      <div v-if="rx.frequency.morning">
                        {{ rx.frequency.morning.nos }}
                      </div>
                      <div v-if="!rx.frequency.morning">--</div>
                    </div>
                  </div>
                </div>
                <div class="col-3 col-md-3">
                  <div class="row">
                    <div class="col-12 col-md-12">
                      <label class="label-style">{{
                        $t("message.Afternoon")
                      }}</label>
                    </div>
                    <div class="col-12 col-md-12">
                      <div v-if="rx.frequency.afternoon">
                        {{ rx.frequency.afternoon.nos }}
                      </div>
                      <div v-if="!rx.frequency.afternoon">--</div>
                    </div>
                  </div>
                </div>
                <div class="col-3 col-md-3">
                  <div class="row">
                    <div class="col-12 col-md-12">
                      <label class="label-style">{{
                        $t("message.Evening")
                      }}</label>
                    </div>
                    <div class="col-12 col-md-12">
                      <div v-if="rx.frequency.evening">
                        {{ rx.frequency.evening.nos }}
                      </div>
                      <div v-if="!rx.frequency.evening">--</div>
                    </div>
                  </div>
                </div>
                <div class="col-3 col-md-3">
                  <div class="row">
                    <div class="col-12 col-md-12">
                      <label class="label-style">{{
                        $t("message.Night")
                      }}</label>
                    </div>
                    <div class="col-12 col-md-12">
                      <div v-if="rx.frequency.night">
                        {{ rx.frequency.night.nos }}
                      </div>
                      <div v-if="!rx.frequency.night">--</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-12 col-md-12"></div>
            <div class="col-12 col-md-12">
              <div class="row">
                <div class="col-4 col-md-4">
                  <div class="row">
                    <div class="col-12 col-md-12">
                      <label class="label-style">{{
                        $t("message.Duration")
                      }}</label>
                    </div>
                    <div class="col-12 col-md-12">
                      <div>{{ rx.frequency.duration }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-4 col-md-4">
                  <div class="row">
                    <div class="col-12 col-md-12">
                      <label class="label-style">{{
                        $t("message.No(number)")
                      }}</label>
                    </div>
                    <div class="col-12 col-md-12">
                      <div>{{ rx.frequency.durationNo }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-4 col-md-4">
                  <div class="row">
                    <div class="col-12 col-md-12">
                      <label class="label-style">{{
                        $t("message.Day(s)")
                      }}</label>
                    </div>
                    <div class="col-12 col-md-12">
                      <div>{{ rx.frequency.day }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-12 col-md-12">
              <div class="row">
                <div class="col-6 col-md-6">
                  <div class="row">
                    <div class="col-12 col-md-12">
                      <label class="label-style">{{
                        $t("message.Minute")
                      }}</label>
                    </div>
                    <div class="col-12 col-md-12">
                      <div>{{ rx.frequency.minutes }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-6 col-md-6">
                  <div class="row">
                    <div class="col-12 col-md-12">
                      <label class="label-style">{{
                        $t("message.When")
                      }}</label>
                    </div>
                    <div class="col-12 col-md-12">
                      <div>{{ rx.frequency.when }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-12 col-md-12" v-if="rx.instruction">
              <div class="row">
                <div class="col-12 col-md-12">
                  <label class="label-style">{{
                    $t("message.More Details")
                  }}</label>
                </div>
                <div class="col-12 col-md-12">
                  <div>{{ rx.instruction }}</div>
                </div>
              </div>
            </div>
          </card>
        </div>
      </div>

      <!-- Lab Prescriptions -->
      <div class="row" v-if="pres.prescriptions.labPrescription.length > 0">
        <div class="col-12 col-md-12">
          <h5>{{ $t("message.Lab Prescription") }}</h5>
          <card id="labCardOpen" v-for="labTest in pres.prescriptions.labPrescription" :key="labTest.xyz">
            <div class="col-12 col-md-12">
              <div class="row">
                <div class="col-12 col-md-12">
                  <label class="label-style">{{
                    $t("message.Test")
                  }}</label>
                </div>
                <div class="col-12 col-md-12">
                  <div>{{ labTest.test }}</div>
                </div>
              </div>
            </div>
            <div class="col-12 col-md-12" v-if="labTest.comment">
              <div class="row">
                <div class="col-12 col-md-12">
                  <label class="label-style">{{
                    $t("message.Comment :")
                  }}</label>
                </div>
                <div class="col-12 col-md-12">
                  <div>{{ labTest.comment }}</div>
                </div>
              </div>
            </div>
          </card>
        </div>
      </div>

      <!-- Imaging Prescription -->
      <div class="row" v-if="pres.prescriptions.imagingPrescription &&
        pres.prescriptions.imagingPrescription.types.length > 0
        ">
        <div class="col-12">
          <h5>{{ $t("message.Imaging Prescription") }}</h5>
        </div>
        <div class="col-12">
          <card v-for="imagingType in pres.prescriptions.imagingPrescription.types" class="card-nav-tabs" header-classes="card-header-warning" :key="imagingType.type">
            <div slot="header">{{ imagingType.type }}</div>
            <div>
              <dynamic-image v-if="imagingType.imageData" :file-id="imagingType.imageData">
              </dynamic-image>
            </div>
            <div class="row" style="font-size: x-small">
              <div class="col-12" v-for="option in imagingType.option" :key="option">
                <i class="fa fa-check-square"></i>
                {{ option }}
              </div>

              <span v-if="imagingType.comment" class="col-12">
                <span>{{ $t("message.Comment :") }}</span>
                {{ imagingType.comment }}
              </span>
            </div>
          </card>
        </div>
      </div>

      <!-- Diet Plan -->
      <div class="row" v-if="hasDietPlan(pres)">
        <div class="col-12">
          <h5>{{ $t("message.Diet Plan") }}</h5>
        </div>

        <!-- Daily Plan -->
        <div class="col-12 p-0" v-if="pres.prescriptions.dietPlan.type == 'daily'">
          <daily-diet-plan :daily-plan="pres.prescriptions.dietPlan.dailyPlan"></daily-diet-plan>
        </div>

        <!-- Weekly Plan -->
        <div v-if="pres.prescriptions.dietPlan.type == 'weekly'">
          <collapse :multiple-active="false" :active-index="0">
            <collapse-item :title="day | capitalCase" v-for="(plan, day) in pres.prescriptions.dietPlan.weeklyPlan" :key="day">
              <div class="col-12 p-0">
                <daily-diet-plan :daily-plan="plan"></daily-diet-plan>
              </div>
            </collapse-item>
          </collapse>
        </div>

        <br />

        <!-- Notes -->
        <div class="col-12 mt-2" v-if="pres.prescriptions.dietPlan.notes">
          <span style="font-size: large">{{ $t("message.Notes") }}:</span>
          <span class="pl-2">{{ pres.prescriptions.dietPlan.notes }}</span>
        </div>

        <!-- Tips -->
        <div class="col-12 mt-2" v-if="pres.prescriptions.dietPlan.tips.length > 0">
          <span style="font-size: large">{{ $t("message.Tips") }}</span>
          <div class="col-12" v-for="(tip, index) in pres.prescriptions.dietPlan.tips">
            {{ index + 1 }}. {{ tip }}
          </div>
        </div>
      </div>

      <br />
      <!-- Attchments -->
      <div class="row" v-if="pres.prescriptions.attachments &&
        pres.prescriptions.attachments.length > 0
        ">
        <div class="col-12">
          <h5>{{ $t("message.Attachments") }}</h5>
        </div>

        <div class="col-12" v-for="attachment in pres.prescriptions.attachments">
          <card @click.native="viewFile(attachment)">
            <div class="row">
              <!-- Thumbnail -->
              <div class="col-2 pr-0">
                <dynamic-image v-if="attachment.type.indexOf('image') > -1" :file-id="attachment.fileId">
                </dynamic-image>
                <i class="fa fa-file-pdf-o" style="font-size: 2em" v-if="attachment.type.indexOf('pdf') > -1"></i>
              </div>
              <div class="col-10" style="
                  white-space: nowrap;
                  text-overflow: ellipsis;
                  overflow: hidden;
                  margin: auto;
                ">
                {{ attachment.name }}
              </div>
            </div>
          </card>
        </div>
      </div>

      <br />

      <!-- Other comments -->
      <div class="row" v-if="pres.prescriptions.otherComments">
        <div class="col-12">
          <h5>{{ $t("message.Other Comments") }}</h5>
          <div>{{ pres.prescriptions.otherComments }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Collapse, CollapseItem } from "src/components";
import DailyDietPlan from "src/pages/carenation/components/DailyDietPlan.vue";
import PrescriptionService from "../../../services/prescription.js";

import NotFoundSvg from "../components/svg_images/NotFoundSvg.vue";

export default {
  name: "view-prescription",

  components: {
    NotFoundSvg,
    Collapse,
    CollapseItem,
    "daily-diet-plan": DailyDietPlan,
  },

  props: {
    prescription_: "",
    appointment: "",
  },

  data() {
    return {
      provider: null,
    };
  },

  computed: {
    prescription() {
      return this.prescription_ || this.$store.state.prescription;
    },
  },

  async mounted() {
    try {
      this.provider = await this.$store.dispatch(
        "getUserById",
        this.appointment.provider.providerId
      );
    } catch (e) {
      console.log(e);
    }
  },

  methods: {
    getShortDosageName(dosageName) {
      if (dosageName) {
        var dosName = "";
        var dName = dosageName.split(",");

        dName.forEach((value, index) => {
          value = value.trim();
          if (index == 0) {
            dosName = dosName + value.substr(0, 3);
          } else {
            dosName = dosName + ", " + value.substr(0, 3);
          }
        });
        return dosName;
      }
    },
    async viewFile(file) {
      if (file.type.indexOf("image") > -1) {
        PhotoViewer.show(this.baseUrl + "/file/" + file.fileId, file.name);
      }

      //If PDF, download and open file
      else if (file.type.indexOf("pdf") > -1) {
        try {
          //get file from server
          let fileArrayBuffer = await this.$store.dispatch(
            "getFile",
            file.fileId
          );

          let pdfBlob = new Blob([fileArrayBuffer]);

          let storageLocation = "";

          if (typeof device !== "undefined") {
            switch (device.platform) {
              case "Android":
                storageLocation = cordova.file.externalDataDirectory;
                break;

              case "iOS":
                storageLocation = cordova.file.documentsDirectory;
                break;
            }

            let folderPath = storageLocation;

            window.resolveLocalFileSystemURL(
              folderPath,
              function (dir) {
                dir.getFile(
                  file.name,
                  {
                    create: true,
                  },
                  function (file) {
                    file.createWriter(
                      function (fileWriter) {
                        fileWriter.write(pdfBlob);

                        fileWriter.onwriteend = function () {
                          var url = file.nativeURL;
                          cordova.plugins.fileOpener2.open(url, pdfBlob.type, {
                            error: function error(err) {
                              console.error(err);
                              alert(this.$t("message.Unable to download"));
                            },
                            success: function success() {
                              console.log("success with opening the file");
                            },
                          });
                        };

                        fileWriter.onerror = function (err) {
                          alert(this.$t("message.Unable to download"));
                          console.error(err);
                        };
                      },
                      function (err) {
                        // failed
                        alert(this.$t("message.Unable to download"));
                        console.error(err);
                      }
                    );
                  },
                  function (err) {
                    alert(this.$t("message.Unable to download"));
                    console.error(err);
                  }
                );
              },
              function (err) {
                alert(this.$t("message.Unable to download"));
                console.error(err);
              }
            );
          }
        } catch (e) {
          console.log(e);
        }
      }
    },
    hasPrescription(prescription) {
      return PrescriptionService.hasPrescription(prescription);
    },
    hasDietPlan(prescription) {
      return PrescriptionService.hasDietPlan(prescription);
    },
  },

  filters: {
    capitalCase: (value) => {
      if (value) {
        return value.toUpperCase();
      }
    },
  },
};
</script>

<style scoped>
/deep/ .card-collapse .card .card-body {
  padding-left: 0px;
  padding-right: 0px;
}

/deep/ .card-collapse .card .card-header a[data-toggle="collapse"] {
  padding-left: 18px;
}

/deep/.card-collapse .card .card-header a[data-toggle="collapse"] i {
  padding-right: 10px;
}

/deep/.card-collapse .card .card-header a[aria-expanded="true"] i {
  padding-left: 10px;
}

/deep/.card-collapse .card .card-header a[data-toggle="collapse"] {
  text-decoration: none;
}

/deep/.card-collapse .card {
  margin-bottom: 0px;
}
</style>
